import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { useMediaState } from '@/hooks/media';
import { Button, DatePicker, Input, Select, Switch } from '@/ui';
import type { WorkerQualification } from '@src/entities/workers';
import { useClientsMap } from '@src/entities/dictionaries/queries';
import phoneFormatter from '@src/modules/login/phone-formatter';

import {
  type QualificationFormValues,
  useQualificationSchema,
} from '../lib/useQualificationSchema';

import css from './styles.module.scss';

interface WorkerQualificationFormProps {
  workerId: string;
  workerName: string;
  workerPhone?: string;
  workerInn?: string;
  workerContractor?: string;
  initialData?: WorkerQualification | null;
  onSubmit: (data: QualificationFormValues) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

function WorkerQualificationForm({
  workerName,
  workerPhone,
  workerInn,
  workerContractor,
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: WorkerQualificationFormProps) {
  const { isMobile } = useMediaState();
  const schema = useQualificationSchema();
  const { data: brandsSource, isFetching: isLoadingBrands } = useClientsMap();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<QualificationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      brands: [],
      quitAt: null,
      certification: false,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (initialData) {
      const quitDate = initialData.quitAt ? dayjs(initialData.quitAt) : null;
      reset({
        brands: initialData.brands ?? [],
        quitAt: quitDate,
        certification: initialData.certification ?? false,
      });
    }
  }, [initialData, reset]);

  const handleDateChange = (date: Dayjs | null) => {
    setValue('quitAt', date, { shouldValidate: true });
  };

  const handleFormSubmit = (data: QualificationFormValues) => {
    onSubmit(data);
  };

  return (
    <div className={css.container}>
      <div className={css.title}>{workerName}</div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className={css.form}>
        <div className={css.fieldsWrapper}>
          <div className={css.infoFields}>
            {isMobile ?
              <>
                <div className={css.infoText}>
                  <span className={css.infoLabel}>Телефон:</span>
                  <span>{phoneFormatter(workerPhone) || '—'}</span>
                </div>
                <div className={css.infoText}>
                  <span className={css.infoLabel}>ИНН:</span>
                  <span>{workerInn || '—'}</span>
                </div>
                <div className={css.infoText}>
                  <span className={css.infoLabel}>Контрагент:</span>
                  <span>{workerContractor || '—'}</span>
                </div>
              </>
            : <>
                <Input
                  label="Телефон"
                  value={phoneFormatter(workerPhone) ?? ''}
                  disabled
                  allowClear={false}
                  onChangeValue={() => {}}
                />
                <Input
                  label="ИНН"
                  value={workerInn ?? ''}
                  disabled
                  allowClear={false}
                  onChangeValue={() => {}}
                />
                <Input
                  label="Контрагент"
                  value={workerContractor ?? ''}
                  disabled
                  allowClear={false}
                  onChangeValue={() => {}}
                />
              </>
            }
          </div>

          <div className={css.editableFields}>
            <Controller
              name="brands"
              control={control}
              render={({ field }) => (
                <Select
                  label="Бренд"
                  placeholder="Выберите бренд"
                  source={brandsSource}
                  loading={isLoadingBrands}
                  allowClear
                  value={field.value?.[0]}
                  error={errors.brands?.message}
                  onChange={(value) =>
                    setValue('brands', value ? [value] : [], {
                      shouldValidate: true,
                    })
                  }
                />
              )}
            />

            <div className={css.datePicker}>
              <Controller
                name="quitAt"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Дата увольнения"
                    value={field.value}
                    onChange={handleDateChange}
                    error={errors.quitAt?.message}
                  />
                )}
              />
            </div>

            <div className={css.switchField}>
              <Controller
                name="certification"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Сертификат"
                    checked={field.value}
                    onChange={(checked) =>
                      setValue('certification', checked, {
                        shouldValidate: true,
                      })
                    }
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className={css.buttons}>
          <Button color="gray" onClick={onCancel} disabled={isLoading}>
            Отменить
          </Button>
          <Button htmlType="submit" disabled={isLoading} loading={isLoading}>
            Сохранить
          </Button>
        </div>
      </form>
    </div>
  );
}

export default WorkerQualificationForm;
